import React, { useEffect, useRef, useState } from 'react';
import { Link } from "@/components/common/Link";
import { Scale, ArrowRight, FileText, Clock, ShieldCheck, Gavel, BookOpen } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function IndividualApplications() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const steps = [{
    title: "Olağan Hukuk Yollarının Tüketilmesi",
    description: "Bireysel başvuru öncesinde, ilk derece mahkemeleri, istinaf ve temyiz aşamalarının eksiksiz tamamlanmış olması zorunludur.",
    icon: <Gavel className="w-6 h-6 text-[#C5A059]" />
  }, {
    title: "Süre Takibi ve Başvuru Hazırlığı",
    description: "Nihai kararın tebliğinden itibaren AYM için 30 gün, AİHM için 4 aylık hak düşürücü süreler titizlikle takip edilir.",
    icon: <Clock className="w-6 h-6 text-[#C5A059]" />
  }, {
    title: "Kabul Edilebilirlik İncelemesi",
    description: "Başvurunun şekil ve esas yönünden mahkemenin belirlediği katı kriterlere uygunluğu teknik bir dille gerekçelendirilir.",
    icon: <ShieldCheck className="w-6 h-6 text-[#C5A059]" />
  }, {
    title: "Esas İnceleme ve İhlal İddiaları",
    description: "Anayasal haklar ve Avrupa İnsan Hakları Sözleşmesi kapsamındaki ihlaller, emsal kararlarla desteklenerek sunulur.",
    icon: <FileText className="w-6 h-6 text-[#C5A059]" />
  }, {
    title: "Karar, Tazminat ve İnfaz",
    description: "İhlal tespiti durumunda yeniden yargılama veya tazminat taleplerinin takibi ve kararın infaz süreci yönetilir.",
    icon: <Scale className="w-6 h-6 text-[#C5A059]" />
  }];
  return <section id="bireysel-başvurular-aymaihm" ref={sectionRef} className="dark bg-linear-to-b from-[#0A1122] to-[#151B28] text-foreground py-24 relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl opacity-50" />
                <div className="absolute bottom-[10%] -left-[10%] w-[400px] h-[400px] bg-secondary/5 rounded-full blur-3xl opacity-40" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="inline-flex items-center justify-center p-2 bg-[#C5A059]/10 rounded-full mb-4">
                        <Scale className="w-5 h-5 text-[#C5A059] mr-2" />
                        <span className="text-sm font-medium text-[#C5A059] tracking-wide uppercase">Yüksek Yargı Başvuruları</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight text-[#CCCCCC] font-serif">
                        Anayasa Mahkemesi ve AİHM <br className="hidden md:block" /> Bireysel Başvuruları
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                        Temel hak ve özgürlüklerinizin ihlali durumunda, ulusal ve uluslararası yargı mercileri nezdinde hak arama sürecinizi profesyonelce yönetiyoruz.
                    </p>
                </div>

                <div className={`transition-all duration-1000 delay-200 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    {}
                    <Card className="bg-card border-2 border-primary/20 shadow-2xl overflow-hidden">
                        <CardHeader className="border-b border-border/50 bg-muted/20 py-8 px-6 md:px-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <CardTitle className="text-2xl font-semibold text-[#CCCCCC] font-serif flex items-center gap-3">
                                        <BookOpen className="w-6 h-6 text-[#C5A059]" />
                                        Başvuru Prosedürü ve Süreç Yönetimi
                                    </CardTitle>
                                    <CardDescription className="text-muted-foreground mt-2 text-base">
                                        Bireysel başvurularda izlediğimiz teknik yol haritası ve aşamalar.
                                    </CardDescription>
                                </div>
                                <div className="hidden md:block">
                                    <Link to="https://celikhukuk.net/iletisim/">
                                        <Button variant="outline" className="border-primary/30 hover:bg-primary/10 hover:text-primary">
                                            Detaylı Bilgi Al
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </CardHeader>
                        
                        <CardContent className="p-6 md:p-10">
                            <div className="grid gap-8 md:gap-12">
                                {steps.map((step, i) => <div key={i} data-index={i} className="group relative flex gap-6 md:gap-8 items-start">
                                        {}
                                        <div className="flex flex-col items-center">
                                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-background border-2 border-[#C5A059]/30 text-[#C5A059] font-bold text-lg shadow-sm group-hover:border-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-primary-foreground transition-colors duration-300 shrink-0 z-10">
                                                {i + 1}
                                            </div>
                                            {i !== steps.length - 1 && <div className="w-px h-full bg-border/50 absolute top-12 left-6 -translate-x-1/2 -z-0 group-hover:bg-[#C5A059]/20 transition-colors duration-300 min-h-[4rem]" />}
                                        </div>

                                        {}
                                        <div className="flex-1 pt-1 pb-4">
                                            <h3 className="text-xl font-semibold text-[#CCCCCC] font-serif mb-2 group-hover:text-[#C5A059] transition-colors duration-300 flex items-center gap-2">
                                                {step.icon}
                                                {step.title}
                                            </h3>
                                            <p className="text-muted-foreground leading-relaxed text-base md:text-lg font-sans">
                                                {step.description}
                                            </p>
                                        </div>
                                    </div>)}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className={`mt-12 text-center transition-all duration-700 delay-400 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <p className="text-muted-foreground mb-6 max-w-2xl mx-auto font-sans">
                        Hak ihlali iddialarınızın AYM ve AİHM standartlarında değerlendirilmesi için uzman ekibimizle iletişime geçebilirsiniz.
                    </p>
                    <Link to="https://celikhukuk.net/iletisim/">
                        <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg px-8 py-6 text-lg h-auto">
                            Hukuki Danışmanlık Talep Et <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </Link>
                </div>
            </div>
        </section>;
}